/**
 * Shared presentation helpers for the Domains dashboard (DEV-121): status styling,
 * the status pill, and money formatting. Kept in one place so the dashboard page
 * and the detail drawer stay visually consistent.
 */
import { classNames } from '~/utils/classNames';

/** Statuses that have reached a resting state — no live polling / pulse needed. */
export const TERMINAL = new Set(['active', 'failed', 'registered', 'connected']);

/** A purchase is still moving through fulfillment (show it under "In progress"). */
export const IN_PROGRESS = new Set(['pending', 'paid', 'registering', 'transferring', 'registered']);

// Canonical flat card — matches ProjectCard/Projects.tsx: bg-surface, hairline
// border, rounded-xl, no glow/shadow. (Was a heavier rounded-2xl bg-raised with
// a drop shadow, which read as "designed" rather than native to the app.)
export const CARD = 'rounded-xl border border-hairline bg-surface';

export const STATUS: Record<string, { label: string; text: string; dot: string }> = {
  pending: { label: 'Awaiting payment', text: 'text-muted border-subtle', dot: 'bg-faint' },
  paid: { label: 'Processing', text: 'text-info border-info/30', dot: 'bg-info' },
  registering: { label: 'Registering', text: 'text-info border-info/30', dot: 'bg-info' },
  // Inbound transfers settle at the registries' pace — often days.
  transferring: { label: 'Transferring in', text: 'text-info border-info/30', dot: 'bg-info' },
  registered: { label: 'Registered', text: 'text-warning border-warning/30', dot: 'bg-warning' },
  active: { label: 'Active', text: 'text-success border-success/30', dot: 'bg-success' },
  connected: { label: 'Connected', text: 'text-success border-success/30', dot: 'bg-success' },
  failed: { label: 'Failed', text: 'text-danger border-danger/30', dot: 'bg-danger' },
  expired: { label: 'Expired', text: 'text-danger border-danger/30', dot: 'bg-danger' },
};

export function StatusPill({ status }: { status: string }) {
  const s = STATUS[status] ?? { label: status, text: 'text-muted border-subtle', dot: 'bg-faint' };
  const pulse = !TERMINAL.has(status) && status !== 'pending';

  return (
    <span className={classNames('ui-pill gap-1.5', s.text)}>
      <span className={classNames('ui-dot', s.dot, pulse && 'animate-pulse')} />
      {s.label}
    </span>
  );
}

export function fmtMoney(amount: number | null, currency = 'usd'): string {
  if (amount == null) {
    return '—';
  }

  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

/** "Jul 15, 2026" — a short absolute date, or a fallback dash. */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) {
    return '—';
  }

  const d = new Date(iso);

  if (Number.isNaN(d.getTime())) {
    return '—';
  }

  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** "Jul 15, 2026, 4:32 PM" — date + time, for the activity timeline. */
export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) {
    return '';
  }

  const d = new Date(iso);

  if (Number.isNaN(d.getTime())) {
    return '';
  }

  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
